/**
 * LoadingPerimeterRing - Rotating four-colour perimeter ring shown around
 * any graph node element while its related-data fetch is in flight.
 *
 * Why an SVG perimeter stroke (not a CSS conic-gradient)?
 *   A `conic-gradient` projects colour bands at fixed angles from the
 *   centre. On a wide rectangle (most graph nodes are 4–5:1) those
 *   angular bands hit the long edges across most of their length and
 *   the short edges almost as a single point — producing big colour
 *   patches on the long sides and slivers on the short sides instead
 *   of even-length lines following the border. SVG strokes follow the
 *   geometry of the rounded rect exactly, so each coloured segment
 *   occupies the same arclength regardless of the host's aspect ratio
 *   *and* regardless of its corner radius (works for both `borderRadius:
 *   10` rounded rect cards and `borderRadius: 999` user pills).
 *
 * Self-tracking host size
 *   The component grabs a `ResizeObserver` on its parent DOM node, so
 *   it tracks the host's *actual* rendered size on every frame the host
 *   resizes — independently of any framework state. Drop the component
 *   into any `position: relative` host and it will paint a ring along
 *   the host's perimeter at all times, regardless of what causes the
 *   resize (content change, hover-state expansion, font load, etc.).
 *
 * Lifecycle
 *   The ring renders iff `ExpandingNodeIdsAtom` contains the host
 *   node's id. `ExpandNodeAtom` adds the id when a fetch starts and
 *   removes it in its `finally` block when the fetch completes (success
 *   or failure), so the ring follows the real fetch lifecycle without
 *   any host-side plumbing.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- The SVG ring is sized from a per-frame ResizeObserver; migrating to css/cssMap would require extra refactoring without visual benefit. */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Atlassian brand palette hex values are part of the design spec for this loading affordance and intentionally bypass token lookup. */

import { memo, useLayoutEffect, useRef, useState, type MemoExoticComponent } from 'react';

import { useAtomValue } from 'jotai';

import { getDocument } from '@atlaskit/browser-apis';
import { jsx } from '@atlaskit/css';

import { ExpandingNodeIdsAtom } from '../atoms/layoutAtoms';

// ── Constants ────────────────────────────────────────────────────────────

// Atlassian brand palette used for the rotating ring (saffron, lime,
// blue, purple). Order matters: this is the order the four arcs sit
// around the perimeter and slide in.
const BRAND_BLUE = '#1868DB';
const BRAND_PURPLE = '#BF63F3';
const BRAND_SAFFRON = '#FCA700';
const BRAND_LIME = '#94C748';

const LOADING_BORDER_COLORS = [BRAND_SAFFRON, BRAND_LIME, BRAND_BLUE, BRAND_PURPLE] as const;

// Stroke thickness of the rotating ring in CSS px. Matches the static
// 1px host border plus a tiny extra so the gradient reads as a deliberate
// accent rather than a hairline.
const LOADING_RING_THICKNESS = 2;

// Animation duration for one full lap around the perimeter. ~3s is a
// smooth, calm loading affordance that doesn't compete with content.
const LOADING_SPIN_DURATION_MS = 3000;

// Each colour gets exactly 25% of the perimeter visible (since
// `pathLength=100` on the rect, units are percent-of-perimeter). The
// remaining 75% is the dash gap. Animating `stroke-dashoffset` from 0 →
// -100 slides the visible arc one full lap.
const SEGMENT_LEN = 25;
const GAP_LEN = 75;

const LOADING_SPIN_ANIMATION = 'twg-graph-node-loading-spin-perim';
const LOADING_SPIN_KEYFRAMES = `@keyframes ${LOADING_SPIN_ANIMATION} { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -100; } }`;

// ── Keyframes injection ──────────────────────────────────────────────────

// Inject the keyframes once per page load. ReactFlow renders nodes
// inside a portal that doesn't pick up component-scoped CSS, so we add
// a tiny `<style>` tag to <head> the first time the ring mounts.
// Idempotent via a `data-twg-loading-keyframes` marker.
let loadingKeyframesInjected = false;
function ensureLoadingKeyframesInjected(): void {
	if (loadingKeyframesInjected) return;
	const doc = getDocument();
	if (!doc) return;
	if (doc.head.querySelector('style[data-twg-loading-keyframes]')) {
		loadingKeyframesInjected = true;
		return;
	}
	const styleEl = doc.createElement('style');
	styleEl.setAttribute('data-twg-loading-keyframes', 'true');
	styleEl.appendChild(doc.createTextNode(LOADING_SPIN_KEYFRAMES));
	doc.head.appendChild(styleEl);
	loadingKeyframesInjected = true;
}

// ── Public hook for hosts ────────────────────────────────────────────────

/**
 * Returns whether the host node should currently render the loading
 * ring. Hosts use this to swap their static border to transparent so
 * the ring doesn't get a faint outline peeking through underneath.
 */
export function useIsNodeLoadingData(nodeId: string): boolean {
	const expanding = useAtomValue(ExpandingNodeIdsAtom);
	return expanding.has(nodeId);
}

// ── Component ────────────────────────────────────────────────────────────

interface LoadingPerimeterRingProps {
	/**
	 * The host node's ReactFlow id. The ring renders iff this id is in
	 * `ExpandingNodeIdsAtom` (i.e. an in-flight `ExpandNodeAtom` call
	 * for this node).
	 */
	nodeId: string;
	/**
	 * Outer corner radius of the host card in CSS px. The SVG `<rect>`
	 * uses this as `rx` so the ring follows the host's curvature. SVG
	 * automatically clamps `rx` to half the rect's short side, so passing
	 * very large values (e.g. `999` for a fully-rounded pill) is safe.
	 *
	 * Examples:
	 *   • Object node card → 10
	 *   • User pill        → 999
	 */
	borderRadius: number;
}

/**
 * Internal: read a DOM element's current size in CSS px.
 *
 * Returns `null` when the element isn't mounted or has zero size yet.
 */
function readSize(el: HTMLElement | null): { width: number; height: number } | null {
	if (!el) return null;
	const r = el.getBoundingClientRect();
	if (r.width <= 0 || r.height <= 0) return null;
	return { width: r.width, height: r.height };
}

export const LoadingPerimeterRing: MemoExoticComponent<({ nodeId, borderRadius }: LoadingPerimeterRingProps) => JSX.Element | null> = memo(
	({ nodeId, borderRadius }: LoadingPerimeterRingProps): JSX.Element | null => {
		const isLoading = useIsNodeLoadingData(nodeId);

		// We render an absolutely-positioned anchor div inside the host
		// (which is `position: relative`). Its `parentElement` is the
		// host itself, which we observe with a `ResizeObserver` so the
		// SVG always reflects the host's true on-screen size — no matter
		// what causes the host to grow or shrink (content change, hover
		// expansion, font load, ReactFlow reflow, etc.). This means the
		// component "just works" for any future host without props.
		const anchorRef = useRef<HTMLDivElement | null>(null);
		const [hostSize, setHostSize] = useState<{ width: number; height: number } | null>(null);

		useLayoutEffect(() => {
			if (!isLoading) return;
			ensureLoadingKeyframesInjected();
			const anchor = anchorRef.current;
			const host = anchor?.parentElement ?? null;
			if (!host) return;

			// Seed once synchronously so the ring paints on first frame
			// instead of waiting for the first ResizeObserver callback.
			setHostSize(readSize(host));

			// Use the global ResizeObserver. We guard for SSR / very old
			// environments where it may be undefined (in which case the
			// ring still renders at its initial size).
			const RO =
				typeof globalThis !== 'undefined'
					? (globalThis as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
					: undefined;
			if (!RO) return;

			const observer = new RO((entries) => {
				for (const entry of entries) {
					// Prefer `borderBoxSize` when available (matches getBoundingClientRect).
					// Fallback to contentRect for older WebKit.
					const box = entry.borderBoxSize?.[0];
					const width = box ? box.inlineSize : entry.contentRect.width;
					const height = box ? box.blockSize : entry.contentRect.height;
					if (width > 0 && height > 0) {
						setHostSize((prev) =>
							prev && prev.width === width && prev.height === height ? prev : { width, height },
						);
					}
				}
			});
			observer.observe(host);
			return () => observer.disconnect();
		}, [isLoading]);

		if (!isLoading) {
			// Render nothing while not loading — also skips the
			// ResizeObserver attach above, so no per-frame cost when
			// idle.
			return null;
		}

		// While we're waiting for the first measurement, render the
		// invisible anchor so the layout effect can latch onto its
		// parent and the SVG appears on the next frame.
		if (!hostSize) {
			return (
				<div
					ref={anchorRef}
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: 0,
						pointerEvents: 'none',
						zIndex: 0,
					}}
				/>
			);
		}

		const { width: cardBoxW, height: cardBoxH } = hostSize;
		const ringWidth = cardBoxW + LOADING_RING_THICKNESS * 2;
		const ringHeight = cardBoxH + LOADING_RING_THICKNESS * 2;

		// SVG stroke is centred on the path. To keep the *outer* edge of
		// the ring flush with the (host + thickness) box, inset the rect
		// by half the stroke width on every side.
		const halfStroke = LOADING_RING_THICKNESS / 2;
		const rectX = halfStroke;
		const rectY = halfStroke;
		const rectW = ringWidth - LOADING_RING_THICKNESS;
		const rectH = ringHeight - LOADING_RING_THICKNESS;

		// The host's outer corner radius equals `borderRadius + thickness`
		// (the ring sits one ring-thickness outside the host). The path
		// radius (centre line of the stroke) is therefore
		// `(borderRadius + thickness) - halfStroke = borderRadius + halfStroke`.
		//
		// IMPORTANT: SVG clamps `rx` to width/2 and `ry` to height/2
		// **independently**. For a wide rectangle that means a large
		// requested radius (e.g. 999 for a pill) becomes
		//   rx = width/2,  ry = height/2
		// → corners drawn as flattened ellipses (a rugby/oval shape),
		// not pill semicircles. To get a true pill we must clamp the
		// radius ourselves to half the SHORTER side so both rx and ry
		// end up equal and the corners draw as perfect circles.
		const requestedRadius = borderRadius + halfStroke;
		const maxPillRadius = Math.min(rectW, rectH) / 2;
		const pathRadius = Math.min(requestedRadius, maxPillRadius);

		return (
			<div
				ref={anchorRef}
				aria-hidden="true"
				style={{
					position: 'absolute',
					inset: 0,
					pointerEvents: 'none',
					zIndex: 0,
				}}
			>
				<svg
					width={ringWidth}
					height={ringHeight}
					viewBox={`0 0 ${ringWidth} ${ringHeight}`}
					style={{
						position: 'absolute',
						top: -LOADING_RING_THICKNESS,
						left: -LOADING_RING_THICKNESS,
						pointerEvents: 'none',
						overflow: 'visible',
					}}
				>
					{LOADING_BORDER_COLORS.map((color, i) => (
						<rect
							key={color}
							x={rectX}
							y={rectY}
							width={rectW}
							height={rectH}
							rx={pathRadius}
							ry={pathRadius}
							fill="none"
							stroke={color}
							strokeWidth={LOADING_RING_THICKNESS}
							strokeLinecap="butt"
							pathLength={100}
							strokeDasharray={`${SEGMENT_LEN} ${GAP_LEN}`}
							style={{
								// Each arc needs its own animation phase so
								// the four colours sit equidistant around
								// the perimeter and slide together as one
								// continuous rotating ribbon. We achieve
								// this with a negative `animation-delay`
								// equal to `(duration / 4) * index` — at
								// t=0 each arc has already advanced by `i ×
								// 25%` of the perimeter.
								animation: `${LOADING_SPIN_ANIMATION} ${LOADING_SPIN_DURATION_MS}ms linear infinite`,
								animationDelay: `-${(LOADING_SPIN_DURATION_MS / 4) * i}ms`,
								willChange: 'stroke-dashoffset',
							}}
						/>
					))}
				</svg>
			</div>
		);
	},
);

LoadingPerimeterRing.displayName = 'LoadingPerimeterRing';
